/**
 * AccountActionWizard - Multi-step wizard for admin account actions
 *
 * Used for locking accounts (with email generation) and permanently deleting accounts.
 * Supports both temporary locks and soft-delete scenarios (requires admin approval).
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Lock,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Mail,
  AlertTriangle,
  Clock,
  Shield,
  Check,
} from "lucide-react";

// Lock reason options
const LOCK_REASONS = [
  { value: "terms_violation", label: "Terms of Service Violation", description: "User violated platform terms" },
  { value: "suspicious_activity", label: "Suspicious Activity", description: "Unusual account behavior detected" },
  { value: "payment_issue", label: "Payment Issue", description: "Outstanding payment or billing problem" },
  { value: "account_review", label: "Account Under Review", description: "Account needs admin verification" },
  { value: "user_request", label: "User Request", description: "User requested account suspension" },
  { value: "security_concern", label: "Security Concern", description: "Potential security threat" },
  { value: "other", label: "Other", description: "Custom reason" },
];

// Timeframe options
const TIMEFRAME_OPTIONS = [
  { value: "24h", label: "24 Hours", description: "Short-term temporary lock" },
  { value: "7d", label: "7 Days", description: "One week to resolve" },
  { value: "30d", label: "30 Days", description: "One month to resolve" },
  { value: "indefinite", label: "Indefinite", description: "Until manually unlocked" },
];

interface UserData {
  id: string;
  fullName: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string;
}

export interface LockAccountData {
  reason: string;
  customReason?: string;
  details: string;
  unlockRequirements: string;
  timeframe: string;
  customTimeframe?: string;
  requiresAdminApproval: boolean;
  sendEmail: boolean;
  emailContent: string;
}

interface AccountActionWizardProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserData | null;
  onLockAccount: (data: LockAccountData) => Promise<void>;
  onDeleteAccount: (reason: string) => Promise<void>;
  isLoading?: boolean;
}

type ActionType = "lock" | "delete" | null;
type WizardStep = "action" | "reason" | "timeframe" | "email" | "confirm" | "delete-confirm";

export function AccountActionWizard({
  isOpen,
  onClose,
  user,
  onLockAccount,
  onDeleteAccount,
  isLoading = false,
}: AccountActionWizardProps) {
  // Wizard state
  const [actionType, setActionType] = useState<ActionType>(null);
  const [step, setStep] = useState<WizardStep>("action");

  // Lock account data
  const [lockData, setLockData] = useState<LockAccountData>({
    reason: "",
    customReason: "",
    details: "",
    unlockRequirements: "",
    timeframe: "7d",
    customTimeframe: "",
    requiresAdminApproval: false,
    sendEmail: true,
    emailContent: "",
  });

  // Delete reason
  const [deleteReason, setDeleteReason] = useState("");

  // Track if email has been opened/reviewed
  const [emailReviewed, setEmailReviewed] = useState(false);

  const userName = user?.fullName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "User";

  // Generate email content based on lock data
  const generateEmailContent = () => {
    const reasonLabel = LOCK_REASONS.find(r => r.value === lockData.reason)?.label || lockData.customReason || "Policy violation";
    const timeframeLabel = TIMEFRAME_OPTIONS.find(t => t.value === lockData.timeframe)?.label || lockData.customTimeframe || "Until resolved";

    return `Dear ${userName},

We are writing to inform you that your Trade Loop account has been temporarily locked.

Reason: ${reasonLabel}

${lockData.details ? `Details:\n${lockData.details}\n` : ""}
What you need to do to restore access:
${lockData.unlockRequirements || "Please contact our support team for more information."}

Timeframe: ${timeframeLabel}

${lockData.requiresAdminApproval ? "Note: Once you have completed the required steps, an administrator will review your account before access is restored.\n" : ""}
If you believe this was done in error or have questions, please contact our support team.

Best regards,
The Trade Loop Team`;
  };

  // Update email content when relevant fields change
  const updateEmailContent = () => {
    if (lockData.sendEmail) {
      setLockData(prev => ({ ...prev, emailContent: generateEmailContent() }));
    }
  };

  // Reset wizard state
  const resetWizard = () => {
    setActionType(null);
    setStep("action");
    setLockData({
      reason: "",
      customReason: "",
      details: "",
      unlockRequirements: "",
      timeframe: "7d",
      customTimeframe: "",
      requiresAdminApproval: false,
      sendEmail: true,
      emailContent: "",
    });
    setDeleteReason("");
    setEmailReviewed(false);
  };

  const handleClose = () => {
    resetWizard();
    onClose();
  };

  // Navigation
  const goToStep = (newStep: WizardStep) => {
    if (newStep === "email") {
      updateEmailContent();
    }
    setStep(newStep);
  };

  // Handle action selection
  const handleActionSelect = (action: ActionType) => {
    setActionType(action);
    if (action === "lock") {
      goToStep("reason");
    } else if (action === "delete") {
      goToStep("delete-confirm");
    }
  };

  // Open email in user's local email client via mailto link
  const openEmailClient = (toEmail: string, subject: string, body: string) => {
    const mailtoLink = `mailto:${toEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    // Create a temporary anchor element and click it
    const link = document.createElement('a');
    link.href = mailtoLink;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle "Review Email" button - opens email in client and marks as reviewed
  const handleReviewEmail = () => {
    const userEmail = user?.email;
    const emailContent = lockData.emailContent;

    if (userEmail && emailContent) {
      const subject = `Trade Loop - Account Status Update`;
      openEmailClient(userEmail, subject, emailContent);
      setEmailReviewed(true);
    }
  };

  // Handle lock submission - just locks the account (email already handled separately)
  const handleLockSubmit = async () => {
    await onLockAccount(lockData);
    handleClose();
  };

  // Handle delete submission
  const handleDeleteSubmit = async () => {
    await onDeleteAccount(deleteReason);
    handleClose();
  };

  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case "action":
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Choose an action for <span className="font-semibold">{userName}</span>
            </p>
            <div className="grid grid-cols-1 gap-3">
              <button
                onClick={() => handleActionSelect("lock")}
                className="flex items-start gap-4 p-4 border rounded-lg hover:bg-gray-50 transition-colors text-left"
              >
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Lock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Lock Account</h3>
                  <p className="text-sm text-gray-600">
                    Temporarily suspend access. User can be unlocked later. Generate an email explaining why.
                  </p>
                </div>
              </button>
              <button
                onClick={() => handleActionSelect("delete")}
                className="flex items-start gap-4 p-4 border border-red-200 rounded-lg hover:bg-red-50 transition-colors text-left"
              >
                <div className="p-2 bg-red-100 rounded-lg">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-red-900">Permanently Delete</h3>
                  <p className="text-sm text-red-700">
                    Remove user and all their data forever. This cannot be undone.
                  </p>
                </div>
              </button>
            </div>
          </div>
        );

      case "reason":
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Why is this account being locked?</p>
            <RadioGroup
              value={lockData.reason}
              onValueChange={(value) => setLockData(prev => ({ ...prev, reason: value }))}
              className="space-y-2"
            >
              {LOCK_REASONS.map((reason) => (
                <div
                  key={reason.value}
                  className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    lockData.reason === reason.value ? "border-blue-500 bg-blue-50" : "hover:bg-gray-50"
                  }`}
                  onClick={() => setLockData(prev => ({ ...prev, reason: reason.value }))}
                >
                  <RadioGroupItem value={reason.value} id={reason.value} className="mt-1" />
                  <div>
                    <Label htmlFor={reason.value} className="font-medium cursor-pointer">
                      {reason.label}
                    </Label>
                    <p className="text-sm text-gray-500">{reason.description}</p>
                  </div>
                </div>
              ))}
            </RadioGroup>
            {lockData.reason === "other" && (
              <Input
                placeholder="Enter custom reason..."
                value={lockData.customReason}
                onChange={(e) => setLockData(prev => ({ ...prev, customReason: e.target.value }))}
              />
            )}
            <div className="space-y-2">
              <Label htmlFor="details">Additional Details (shown to user)</Label>
              <Textarea
                id="details"
                placeholder="Provide more context about why the account is being locked..."
                value={lockData.details}
                onChange={(e) => setLockData(prev => ({ ...prev, details: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
        );

      case "timeframe":
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">How long should the account be locked?</p>
            <RadioGroup
              value={lockData.timeframe}
              onValueChange={(value) => setLockData(prev => ({ ...prev, timeframe: value }))}
              className="space-y-2"
            >
              {TIMEFRAME_OPTIONS.map((option) => (
                <div
                  key={option.value}
                  className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    lockData.timeframe === option.value ? "border-blue-500 bg-blue-50" : "hover:bg-gray-50"
                  }`}
                  onClick={() => setLockData(prev => ({ ...prev, timeframe: option.value }))}
                >
                  <RadioGroupItem value={option.value} id={option.value} className="mt-1" />
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <div>
                      <Label htmlFor={option.value} className="font-medium cursor-pointer">
                        {option.label}
                      </Label>
                      <p className="text-sm text-gray-500">{option.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </RadioGroup>

            <div className="space-y-2">
              <Label htmlFor="requirements">What does the user need to do to unlock?</Label>
              <Textarea
                id="requirements"
                placeholder="e.g., Contact support with ID verification, Pay outstanding balance, etc."
                value={lockData.unlockRequirements}
                onChange={(e) => setLockData(prev => ({ ...prev, unlockRequirements: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="flex items-start space-x-3 p-3 border rounded-lg bg-amber-50 border-amber-200">
              <Checkbox
                id="adminApproval"
                checked={lockData.requiresAdminApproval}
                onCheckedChange={(checked) =>
                  setLockData(prev => ({ ...prev, requiresAdminApproval: checked === true }))
                }
              />
              <div>
                <Label htmlFor="adminApproval" className="font-medium cursor-pointer flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-600" />
                  Require Admin Approval to Unlock
                </Label>
                <p className="text-sm text-amber-700">
                  User cannot self-restore access. An admin must manually approve unlocking even after
                  requirements are met. Use this for serious violations or account reviews.
                </p>
              </div>
            </div>
          </div>
        );

      case "email":
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">Email notification for the user</p>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="sendEmail"
                  checked={lockData.sendEmail}
                  onCheckedChange={(checked) =>
                    setLockData(prev => ({ ...prev, sendEmail: checked === true }))
                  }
                />
                <Label htmlFor="sendEmail" className="text-sm">Send email</Label>
              </div>
            </div>

            {lockData.sendEmail && (
              <>
                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Mail className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-blue-800 font-medium">Opens in your email client</p>
                    <p className="text-blue-600">
                      To: {user?.email || "user email"}
                    </p>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm text-gray-600">Email content (you can edit before sending)</Label>
                  <Textarea
                    value={lockData.emailContent}
                    onChange={(e) => setLockData(prev => ({ ...prev, emailContent: e.target.value }))}
                    rows={12}
                    className="font-mono text-sm"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={updateEmailContent}
                  className="w-full"
                >
                  Regenerate Email
                </Button>
              </>
            )}
          </div>
        );

      case "confirm":
        const reasonLabel = LOCK_REASONS.find(r => r.value === lockData.reason)?.label || lockData.customReason;
        const timeframeLabel = TIMEFRAME_OPTIONS.find(t => t.value === lockData.timeframe)?.label;

        return (
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <h3 className="font-semibold text-yellow-800">Confirm Account Lock</h3>
              </div>
              <p className="text-sm text-yellow-700">
                You are about to lock <span className="font-semibold">{userName}</span>'s account.
                They will be immediately logged out and unable to sign in.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b">
                <span className="text-sm text-gray-600">Reason</span>
                <span className="text-sm font-medium">{reasonLabel}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-sm text-gray-600">Duration</span>
                <span className="text-sm font-medium">{timeframeLabel}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-sm text-gray-600">Admin Approval Required</span>
                <Badge variant={lockData.requiresAdminApproval ? "destructive" : "secondary"}>
                  {lockData.requiresAdminApproval ? "Yes" : "No"}
                </Badge>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-sm text-gray-600">Send Email</span>
                <Badge variant={lockData.sendEmail ? "default" : "secondary"}>
                  {lockData.sendEmail ? "Opens in your email client" : "No"}
                </Badge>
              </div>
              {lockData.unlockRequirements && (
                <div className="py-2">
                  <span className="text-sm text-gray-600 block mb-1">Unlock Requirements</span>
                  <p className="text-sm bg-gray-50 p-2 rounded">{lockData.unlockRequirements}</p>
                </div>
              )}
            </div>
          </div>
        );

      case "delete-confirm":
        return (
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="font-semibold text-red-800">Permanent Deletion Warning</h3>
              </div>
              <p className="text-sm text-red-700">
                You are about to <span className="font-bold">permanently delete</span>{" "}
                <span className="font-semibold">{userName}</span>'s account.
              </p>
              <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                <li>All user data will be removed</li>
                <li>All connections and referrals will be deleted</li>
                <li>All messages and history will be lost</li>
                <li>This action cannot be undone</li>
              </ul>
            </div>

            <div className="space-y-2">
              <Label htmlFor="deleteReason">Reason for deletion (for audit log)</Label>
              <Textarea
                id="deleteReason"
                placeholder="Why is this account being permanently deleted?"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                rows={3}
              />
            </div>

            <div className="p-3 bg-gray-100 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>Note:</strong> Consider using "Lock Account" instead if you may need to restore
                access later. Locked accounts can require admin approval before being unlocked.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Render footer buttons
  const renderFooter = () => {
    switch (step) {
      case "action":
        return (
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
        );

      case "reason":
        return (
          <>
            <Button variant="outline" onClick={() => goToStep("action")}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button
              onClick={() => goToStep("timeframe")}
              disabled={!lockData.reason || (lockData.reason === "other" && !lockData.customReason)}
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </>
        );

      case "timeframe":
        return (
          <>
            <Button variant="outline" onClick={() => goToStep("reason")}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button onClick={() => goToStep("email")}>
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </>
        );

      case "email":
        return (
          <>
            <Button variant="outline" onClick={() => goToStep("timeframe")}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button onClick={() => goToStep("confirm")}>
              Review <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </>
        );

      case "confirm":
        // If email is enabled and not yet reviewed, show "Review Email" button first
        const needsEmailReview = lockData.sendEmail && !emailReviewed;

        return (
          <>
            <Button variant="outline" onClick={() => goToStep("email")}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            {needsEmailReview ? (
              <Button
                onClick={handleReviewEmail}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Mail className="w-4 h-4 mr-1" /> Review Email
              </Button>
            ) : (
              <Button
                onClick={handleLockSubmit}
                disabled={isLoading}
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                {isLoading ? "Locking..." : (
                  <>
                    <Lock className="w-4 h-4 mr-1" /> Lock Account
                  </>
                )}
              </Button>
            )}
          </>
        );

      case "delete-confirm":
        return (
          <>
            <Button variant="outline" onClick={() => goToStep("action")}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteSubmit}
              disabled={isLoading || !deleteReason.trim()}
            >
              {isLoading ? "Deleting..." : (
                <>
                  <Trash2 className="w-4 h-4 mr-1" /> Permanently Delete
                </>
              )}
            </Button>
          </>
        );

      default:
        return null;
    }
  };

  // Get step title
  const getStepTitle = () => {
    switch (step) {
      case "action":
        return "Account Action";
      case "reason":
        return "Lock Reason";
      case "timeframe":
        return "Lock Duration & Requirements";
      case "email":
        return "Email Notification";
      case "confirm":
        return "Confirm Lock";
      case "delete-confirm":
        return "Confirm Permanent Deletion";
      default:
        return "Account Action";
    }
  };

  // Get progress indicator
  const getProgress = () => {
    if (actionType === "delete") return null;

    const steps = ["reason", "timeframe", "email", "confirm"];
    const currentIndex = steps.indexOf(step);
    if (currentIndex === -1) return null;

    return (
      <div className="flex items-center gap-2 mb-4">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                i < currentIndex
                  ? "bg-green-500 text-white"
                  : i === currentIndex
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 text-gray-600"
              }`}
            >
              {i < currentIndex ? <Check className="w-4 h-4" /> : i + 1}
            </div>
            {i < steps.length - 1 && (
              <div
                className={`w-8 h-1 ${i < currentIndex ? "bg-green-500" : "bg-gray-200"}`}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === "delete-confirm" ? (
              <Trash2 className="w-5 h-5 text-red-600" />
            ) : step !== "action" ? (
              <Lock className="w-5 h-5 text-yellow-600" />
            ) : null}
            {getStepTitle()}
          </DialogTitle>
          {step === "action" && (
            <DialogDescription>
              Manage account status for {userName}
            </DialogDescription>
          )}
        </DialogHeader>

        {getProgress()}

        <div className="py-4">{renderStepContent()}</div>

        <DialogFooter className="flex justify-between gap-2">{renderFooter()}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AccountActionWizard;
